import React, {
  type PropsWithChildren,
  type RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { BlurView } from 'expo-blur';
import { BackHandler, Platform, StyleSheet, View, type ViewInstance } from 'react-native';
import Animated, { Easing, Keyframe, useReducedMotion } from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useStyle } from 'react-native-style-utilities';
import { scheduleOnRN } from 'react-native-worklets';

import { theme } from '@garnish/constants';

import { useWebKeyListener } from '../../../../../../hooks';
import { FocusScope } from '../../../../../FocusScope';
import { GlassView } from '../../../../../GlassView';
import {
  NAVBAR_CONTENT_HEIGHT,
  NAVBAR_MENU_ANIMATION_DURATION,
} from '../../../../Navbar.constants';
import { focusNavbarMenuElement } from '../../NavbarMenu.utils';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Floating modal menu anchored above the navbar's call to action, growing out
 * of its bottom right corner. Closes on Escape (web), the screen reader escape
 * gesture (iOS) and the hardware back button (Android), then hands focus back
 * to `returnFocusRef`.
 */
export const NavbarMenuContainer = (props: Props) => {
  const {
    accessibilityLabel,
    children,
    glassFillOpacity = 0.85,
    isLiquidGlassEnabled = false,
    returnFocusRef,
    withBlur = true,
    onClose,
  } = props;

  const { bottom: safeAreaBottomInset } = useSafeAreaInsets();
  const isReduceMotionEnabled = useReducedMotion();

  // ─── Refs ──────────────────────────────────────────────────────────────────

  const hasDismissedRef = useRef(false);

  // ─── Derived ───────────────────────────────────────────────────────────────

  const shouldAnimate = !isReduceMotionEnabled;
  const shouldRenderGlass = isLiquidGlassEnabled;
  const shouldRenderBlur = !isLiquidGlassEnabled && withBlur;
  const shouldRenderSolidFill = !isLiquidGlassEnabled && !withBlur;

  // ─── Handlers ──────────────────────────────────────────────────────────────

  // NOTE: Only a dismissal hands focus back, since picking an option moves on.
  const returnFocus = useCallback(() => {
    if (!hasDismissedRef.current) {
      return;
    }

    focusNavbarMenuElement(returnFocusRef?.current);
  }, [returnFocusRef]);

  // NOTE: Until the menu has animated out it still traps focus on web and
  //       hides everything around it from screen readers on native.
  const handleExitFinished = useCallback(
    (isFinished: boolean) => {
      'worklet';

      if (!isFinished) {
        return;
      }

      scheduleOnRN(returnFocus);
    },
    [returnFocus],
  );

  // NOTE: Without motion there's no exit animation, so focus returns right
  //       after the menu unmounts.
  const returnFocusAfterUnmount = useCallback(() => {
    return () => {
      if (shouldAnimate) {
        return;
      }

      setTimeout(returnFocus, 0);
    };
  }, [returnFocus, shouldAnimate]);

  const handleDismiss = useCallback(() => {
    hasDismissedRef.current = true;
    onClose();
  }, [onClose]);

  const handleHardwareBackPress = useCallback(() => {
    handleDismiss();

    return true;
  }, [handleDismiss]);

  // NOTE: Only Android has a hardware back button, and `BackHandler` errors on web.
  const subscribeToHardwareBackPress = useCallback(() => {
    if (Platform.OS !== 'android') {
      return;
    }

    const subscription = BackHandler.addEventListener('hardwareBackPress', handleHardwareBackPress);

    return () => {
      subscription.remove();
    };
  }, [handleHardwareBackPress]);

  // ─── Animations ────────────────────────────────────────────────────────────

  const menuExitingAnimation = useMemo(
    () =>
      new Keyframe(MENU_EXITING_KEYFRAMES)
        .duration(NAVBAR_MENU_ANIMATION_DURATION)
        .withCallback(handleExitFinished),
    [handleExitFinished],
  );

  // ─── Effects ───────────────────────────────────────────────────────────────

  useWebKeyListener('Escape', handleDismiss);
  useEffect(subscribeToHardwareBackPress, [subscribeToHardwareBackPress]);
  useEffect(returnFocusAfterUnmount, [returnFocusAfterUnmount]);

  // ─── Styles ────────────────────────────────────────────────────────────────

  // NOTE: Floats the menu just above the navbar content, which is padded by the
  //       bottom safe area inset with a minimum spacing.
  const containerDynamicStyles = useStyle(() => {
    const navbarBottomSpacing = Math.max(safeAreaBottomInset, theme.spacing['6']);

    return { bottom: NAVBAR_CONTENT_HEIGHT + navbarBottomSpacing + theme.spacing['3'] };
  }, [safeAreaBottomInset]);

  const surfaceGlassStyles = useStyle(
    () => ({ backgroundColor: `rgba(255, 255, 255, ${glassFillOpacity})` }),
    [glassFillOpacity],
  );

  const containerStyles = [styles.container, containerDynamicStyles];
  const surfaceStyles = [
    styles.surface,
    shouldRenderGlass ? surfaceGlassStyles : undefined,
    shouldRenderSolidFill ? styles.surfaceSolid : undefined,
  ];

  // ───────────────────────────────────────────────────────────────────────────

  // NOTE: Focus moves in and out with the menu's own transitions, so the scope
  //       only traps it.
  return (
    <FocusScope autoFocus={false} restoreFocus={false}>
      <Animated.View
        aria-label={accessibilityLabel}
        aria-modal
        role="dialog"
        entering={shouldAnimate ? MENU_ENTERING : undefined}
        exiting={shouldAnimate ? menuExitingAnimation : undefined}
        style={containerStyles}
        onAccessibilityEscape={handleDismiss}
      >
        <View style={surfaceStyles}>
          <View style={styles.background}>
            {shouldRenderGlass ? (
              <GlassView
                glassEffectStyle="regular"
                tintColor={theme.colors.OPACITY.BLACK.ALMOST_TRANSPARENT}
                style={styles.backgroundEffect}
              />
            ) : null}

            {shouldRenderBlur ? <BlurView intensity={80} tint="light" style={styles.blur} /> : null}
          </View>

          {children}
        </View>
      </Animated.View>
    </FocusScope>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const MENU_COLLAPSED_SCALE = 0.4;

const MENU_ENTERING = new Keyframe({
  0: {
    opacity: 0,
    transform: [{ scale: MENU_COLLAPSED_SCALE }],
  },
  100: {
    opacity: 1,
    transform: [{ scale: 1 }],
    easing: Easing.out(Easing.cubic),
  },
}).duration(NAVBAR_MENU_ANIMATION_DURATION);

const MENU_EXITING_KEYFRAMES = {
  0: {
    opacity: 1,
    transform: [{ scale: 1 }],
  },
  100: {
    opacity: 0,
    transform: [{ scale: MENU_COLLAPSED_SCALE }],
    easing: Easing.in(Easing.cubic),
  },
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    right: theme.spacing['4'],
    left: theme.spacing['4'],
    alignItems: 'flex-end',
    pointerEvents: 'box-none',
    transformOrigin: 'bottom right',
  },
  surface: {
    width: '100%',
    maxWidth: 400,
    paddingHorizontal: theme.spacing['4'],
    borderRadius: theme.radius.large,
    borderCurve: 'continuous',
    boxShadow: '0px 8px 40px 4px rgba(14, 21, 14, 0.09)',
  },
  surfaceSolid: {
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
  },
  background: {
    ...StyleSheet.absoluteFill,
    borderRadius: theme.radius.large,
    borderCurve: 'continuous',
    overflow: 'hidden',
    pointerEvents: 'none',
  },
  backgroundEffect: {
    ...StyleSheet.absoluteFill,
    borderRadius: theme.radius.large,
    borderCurve: 'continuous',
  },
  blur: {
    ...StyleSheet.absoluteFill,
    backgroundColor: 'rgba(255, 255, 255, 0.96)',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = PropsWithChildren<{
  accessibilityLabel: string;
  glassFillOpacity?: number;
  isLiquidGlassEnabled?: boolean;
  returnFocusRef?: RefObject<ViewInstance | null>;
  withBlur?: boolean;
  onClose: () => void;
}>;
